import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Search } from "lucide-react";
import { FadeUp, SectionHeading, Counter } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { MP_FACTS, MP_REGIONS } from "@/data/mp";

export default function MPExplorer() {
  const [active, setActive] = useState(MP_REGIONS[0].id);
  const [q, setQ] = useState("");

  const region = MP_REGIONS.find((r) => r.id === active);
  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return null;
    return MP_REGIONS.flatMap((r) => r.districts.filter(([d]) => d.toLowerCase().includes(s)).map(([d, n]) => ({ d, n, r })));
  }, [q]);

  return (
    <section className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-32" data-testid="mp-explorer-section">
      <div className="flex flex-wrap items-end justify-between gap-8">
        <SectionHeading overline="Madhya Pradesh — चुनावी नक्शा" lines={["230 seats.", "We know every one."]} testId="mp-heading" />
        <FadeUp delay={0.15}>
          <div className="grid grid-cols-2 gap-x-10 gap-y-6 sm:grid-cols-4">
            {MP_FACTS.map((f) => (
              <div key={f.label} data-testid={`mp-fact-${f.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <Counter to={f.value} suffix={f.suffix} decimals={f.decimals} className="font-display text-3xl font-bold text-paper sm:text-4xl" />
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{f.label}</p>
              </div>
            ))}
          </div>
        </FadeUp>
      </div>

      <FadeUp delay={0.1} className="mt-14">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
              <Input
                data-testid="mp-district-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search a district — Indore, Rewa, Sagar…"
                className="border-white/15 bg-coal/60 pl-10 text-paper placeholder:text-zinc-600 focus-visible:ring-brand"
              />
            </div>
            <div className="mt-4 space-y-2" data-testid="mp-region-list">
              {MP_REGIONS.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  data-testid={`mp-region-${r.id}`}
                  onClick={() => { setActive(r.id); setQ(""); }}
                  className={`group flex w-full items-center justify-between rounded-xl border px-5 py-4 text-left transition-colors duration-300 ${
                    active === r.id && !results ? "border-brand bg-brand/10" : "border-white/10 bg-coal/40 hover:border-white/25"
                  }`}
                >
                  <span>
                    <span className="block font-display text-base font-bold uppercase tracking-tight text-paper">{r.name}</span>
                    <span className="font-hindi text-sm text-saffron">{r.hindi}</span>
                  </span>
                  <span className="text-right">
                    <span className="block font-display text-2xl font-bold text-paper">{r.seats}</span>
                    <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">seats</span>
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="lg:col-span-8">
            <AnimatePresence mode="wait">
              {results ? (
                <motion.div key="search" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.35 }} className="rounded-2xl border border-white/10 bg-ink/60 p-6 lg:p-8" data-testid="mp-search-results">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{results.length} district{results.length === 1 ? "" : "s"} found</p>
                  <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {results.map(({ d, n, r }) => (
                      <div key={d} className="rounded-xl border border-white/10 bg-coal/60 p-4" data-testid={`mp-district-${d.toLowerCase().replace(/\s+/g, "-")}`}>
                        <p className="font-display text-lg font-bold uppercase tracking-tight text-paper">{d}</p>
                        <p className="text-xs text-zinc-500">{r.name}</p>
                        <p className="mt-2 font-display text-2xl font-bold text-saffron">{n} <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">seats</span></p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ) : (
                <motion.div key={region.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.35 }} className="rounded-2xl border border-white/10 bg-ink/60 p-6 lg:p-8" data-testid="mp-region-panel">
                  <div className="flex flex-wrap items-end justify-between gap-6">
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-saffron">Region HQ — {region.hq}</p>
                      <h3 className="mt-2 font-display text-3xl font-bold uppercase tracking-tight text-paper sm:text-4xl">{region.name}</h3>
                    </div>
                    <div className="flex gap-8">
                      <div>
                        <p className="font-display text-3xl font-bold text-paper">{region.seats}</p>
                        <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">Vidhan Sabha seats</p>
                      </div>
                      <div>
                        <p className="font-display text-3xl font-bold text-brand">{region.campaigns}</p>
                        <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">Our campaigns</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                    {region.districts.map(([d, n], i) => (
                      <motion.div
                        key={d}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.03, duration: 0.4 }}
                        className="group rounded-xl border border-white/10 bg-coal/60 p-4 transition-colors duration-300 hover:border-saffron/50"
                        data-testid={`mp-district-${d.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <p className="font-display text-base font-bold uppercase tracking-tight text-paper">{d}</p>
                        <div className="mt-3 flex items-end justify-between">
                          <p className="font-display text-2xl font-bold text-saffron">{n}</p>
                          <div className="flex gap-0.5">
                            {Array.from({ length: n }).map((_, k) => (
                              <span key={k} className="h-3 w-1 rounded-sm bg-saffron/60 transition-colors duration-300 group-hover:bg-saffron" />
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">
              Source: Election Commission of India — 230-seat assembly map. Newer districts shown with parent district.
            </p>
          </div>
        </div>
      </FadeUp>
    </section>
  );
}
